import { goSolidApi } from "@/composables/useGoSolidApi";
import { downloadFromApiResponse } from "@/utils/downloadUtils";
import type {
  CnmcOrderFilings,
  CnmcContext,
  CnmcDevice,
  CnmcFilingProfile,
  CnmcFilingView,
  CnmcPlan,
  CnmcPlanRequest,
  CnmcProductionInput,
  CnmcTemplate,
} from "./cnmcTypes";

/** The route prefix of every connection-scoped CNMC call. */
export const base = (companyId: number, connectionId: number) =>
  `/issuance-reporting/companies/${companyId}/cnmc/connections/${connectionId}`;

/**
 * Connection-scoped CNMC filing API. Failed requests propagate to the caller's global error handler.
 * The `/applications/{id}` routes serve issuance-plan filings and outbound filings of synced holdings
 * alike; receipt entry and replacement answer with the view of whatever the filing belongs to.
 */
export const cnmcApi = {
  order: (companyId: number, orderId: number) =>
    goSolidApi.get<CnmcOrderFilings>(
      `/issuance-reporting/companies/${companyId}/cnmc/orders/${orderId}`
    ),
  context: (companyId: number) =>
    goSolidApi.get<CnmcContext>(
      `/issuance-reporting/companies/${companyId}/cnmc`
    ),
  devices: (companyId: number, connectionId: number) =>
    goSolidApi.get<{ devices: CnmcDevice[] }>(
      `${base(companyId, connectionId)}/devices`
    ),
  uploadReadings: (companyId: number, connectionId: number, file: File) => {
    const data = new FormData();
    data.append("file", file);
    return goSolidApi.post<{ readings: CnmcProductionInput[] }>(
      `${base(companyId, connectionId)}/reading-uploads`,
      data
    );
  },
  templates: (companyId: number, connectionId: number) =>
    goSolidApi.get<{ templates: CnmcTemplate[] }>(
      `${base(companyId, connectionId)}/templates`
    ),
  plans: (companyId: number, connectionId: number, offset = 0) =>
    goSolidApi.get<{ plans: CnmcPlan[]; has_more: boolean }>(
      `${base(companyId, connectionId)}/plans`,
      { params: { offset } }
    ),
  plan: (companyId: number, connectionId: number, planId: number) =>
    goSolidApi.get<CnmcPlan>(
      `${base(companyId, connectionId)}/plans/${planId}`
    ),
  saveProfile: (
    companyId: number,
    connectionId: number,
    data: CnmcFilingProfile
  ) =>
    goSolidApi.put<CnmcFilingProfile>(
      `${base(companyId, connectionId)}/profile`,
      data
    ),
  save: (
    companyId: number,
    connectionId: number,
    data: CnmcPlanRequest,
    planId?: number
  ) =>
    planId
      ? goSolidApi.put<CnmcPlan>(
          `${base(companyId, connectionId)}/plans/${planId}`,
          data
        )
      : goSolidApi.post<CnmcPlan>(
          `${base(companyId, connectionId)}/plans`,
          data
        ),
  deleteDraft: (companyId: number, connectionId: number, planId: number) =>
    goSolidApi.delete<void>(`${base(companyId, connectionId)}/plans/${planId}`),
  confirm: (companyId: number, connectionId: number, planId: number) =>
    goSolidApi.post<CnmcPlan>(
      `${base(companyId, connectionId)}/plans/${planId}/confirmation`
    ),
  delivery: (
    companyId: number,
    connectionId: number,
    planId: number,
    ppaId: number | null
  ) =>
    goSolidApi.post<CnmcPlan>(
      `${base(companyId, connectionId)}/plans/${planId}/deliveries`,
      { ppa_portfolio_id: ppaId }
    ),
  submit: (
    companyId: number,
    connectionId: number,
    applicationId: number,
    receipt: string,
    reason?: string
  ) =>
    goSolidApi.put<CnmcFilingView>(
      `${base(companyId, connectionId)}/applications/${applicationId}/submission`,
      { receipt, correction_reason: reason || null }
    ),
  check: (companyId: number, connectionId: number, applicationId: number) =>
    goSolidApi.post(
      `${base(companyId, connectionId)}/applications/${applicationId}/checks`
    ),
  abandon: (
    companyId: number,
    connectionId: number,
    planId: number,
    reason: string
  ) =>
    goSolidApi.post(
      `${base(companyId, connectionId)}/plans/${planId}/abandonment`,
      { reason }
    ),
  resolve: (
    companyId: number,
    connectionId: number,
    applicationId: number,
    reason: string
  ) =>
    goSolidApi.post(
      `${base(companyId, connectionId)}/applications/${applicationId}/resolution`,
      { reason }
    ),
  replace: (
    companyId: number,
    connectionId: number,
    applicationId: number,
    reason: string
  ) =>
    goSolidApi.post<CnmcFilingView>(
      `${base(companyId, connectionId)}/applications/${applicationId}/replacement`,
      { reason }
    ),
  download: async (
    companyId: number,
    connectionId: number,
    applicationId: number
  ) => {
    const response = await goSolidApi.get(
      `${base(companyId, connectionId)}/applications/${applicationId}/file`,
      { responseType: "blob" }
    );
    downloadFromApiResponse(
      response.data,
      response.headers,
      "CNMC-filing.zip",
      "application/zip"
    );
  },
};
